import {
  AlertCircleFilledIcon,
  CheckmarkFilledIcon,
  LoadSpinner,
  TimeIcon,
  WarningFilledIcon,
} from '@calizahq/react-components-v2'
import clsx from 'clsx'
import React from 'react'
import { useNavigate } from 'react-router-dom'

import { useTimeline } from '@/hooks/useTimeline'
import { TimelineEvent } from '@/types/Homes'
import { formatDate, parseDate } from '@/util'

type Props = {
  homeId: number
}

export const HomeHistory = ({ homeId }: Props) => {
  const navigate = useNavigate()
  const { isLoading, data: homeTimelineData } = useTimeline({
    id: homeId,
  })

  const homeHistoryAction = (event: TimelineEvent) => {
    switch (event.eventType) {
      case 'Reservation': {
        navigate(`/reservations/${event.eventId}`)
        break
      }
      case 'Job': {
        navigate(`/jobs/${event.eventId}`)
        break
      }
      default: {
        return
      }
    }
  }

  return (
    <div className="px-4">
      {isLoading ? (
        <div className="flex h-20 w-full items-center justify-center">
          <LoadSpinner className="icon-secondary" isCurrentColor={true} size={2} />
        </div>
      ) : (
        <div className="mt-4 flex w-full flex-col">
          {homeTimelineData?.home.timeline &&
            homeTimelineData?.home.timeline.map((day, index) => {
              const { date, events } = day
              const formattedDate = formatDate(parseDate(date), 'MMMM d, yyyy')

              return (
                <div className="flex w-full border-b border-secondary" key={index}>
                  <div className="flex flex-col w-1/4 py-6 pr-2">
                    <p className="text-xl leading-120" style={{ overflowWrap: 'break-word' }}>
                      {formattedDate}
                    </p>
                  </div>
                  <div className="flex flex-grow flex-col py-6">
                    {events.map((event, index) => {
                      return (
                        <div
                          className={`flex w-full flex-col items-start rounded-md border border-grey-200 px-6 py-4 ${
                            index === 0 ? '' : 'mt-4'
                          }`}
                          key={index}
                          role="presentation"
                        >
                          <div className="flex" onClick={() => homeHistoryAction(event)} role="presentation">
                            {getHomeHistoryIcon(event.outcome)}
                            <p
                              className={clsx(
                                'mb-1 py-1 text-primary',
                                (event.eventType == 'Job' || event.eventType == 'Reservation') &&
                                  'cursor-pointer hover:underline',
                              )}
                              style={{ overflowWrap: 'break-word' }}
                            >
                              {event.event} {event.eventType === 'Reservation' ? ' for ' : ' by '} {event.party}
                            </p>
                          </div>
                        </div>
                      )
                    })}
                  </div>
                </div>
              )
            })}
        </div>
      )}
    </div>
  )
}

const getHomeHistoryIcon = (outcome: string) => {
  switch (outcome) {
    case 'Pending': {
      return (
        <div title={outcome}>
          <TimeIcon className="mr-3 text-yellow-300" height={28} width={28} />
        </div>
      )
    }
    case 'Incomplete': {
      return (
        <div title={outcome}>
          <WarningFilledIcon className="mr-3 text-yellow-300" height={28} width={28} />
        </div>
      )
    }
    case 'Cancelled': {
      return (
        <div title={outcome}>
          <AlertCircleFilledIcon className="mr-3 text-red-300" height={28} width={28} />
        </div>
      )
    }
    case 'Completed': {
      return (
        <div title={outcome}>
          <CheckmarkFilledIcon className="mr-3 text-green-300" height={28} width={28} />
        </div>
      )
    }
    default: {
      return <></>
    }
  }
}
